/**
 * The record forms' sentences that speak of the client, one builder per
 * sentence, fed by the client's recorded pronouns through pronounForms.
 * Pronouns not recorded, or any text that is not a recorded set, read
 * "this client". Where the staff member's "they" shares a sentence with
 * the client (the care manager and Add staff intros), the they/them
 * client also reads "this client", so the two never collide.
 *
 * ageLine is Edit about's help under Date of birth: the client's age on
 * the practice day.
 */

import { ageOn } from '@/lib/faceSheet';
import { pronounForms } from '@/lib/pronounForms';
import type { ClientPronouns } from '@/lib/clientRecord';

type Pronouns = ClientPronouns | string | null | undefined;

function forms(p: Pronouns): ReturnType<typeof pronounForms> {
  return pronounForms(p as ClientPronouns | null | undefined);
}

/** The forms a two-person sentence uses for the client: "this client" for
 *  they/them as well as for none, so the staff member's "they" stands alone. */
function twoPersonForms(p: Pronouns): ReturnType<typeof pronounForms> {
  const f = forms(p);
  return f.plural ? pronounForms(null) : f;
}

/** Change service: which services the client has. */
export function changeServiceIntro(p: Pronouns): string {
  const f = forms(p);
  const generic = f.subject === 'this client';
  const has = f.plural ? 'have' : 'has';
  const record = generic ? 'the record' : `${f.possessive} record`;
  return `Which services ${f.subject} ${has}. It decides which tabs ${record} shows and which roster views list ${f.object}.`;
}

/** The strip's care manager dialog. */
export function assignCmIntro(p: Pronouns): string {
  const f = twoPersonForms(p);
  return `The care manager owns ${f.possessive} care. Their caseload and this record change as soon as you save.`;
}

/** The strip's Add staff dialog, by service. */
export function addStaffIntro(service: 'care_management' | 'spice', p: Pronouns): string {
  const f = twoPersonForms(p);
  const lead = service === 'care_management' ? 'lead care manager' : 'lead SPICE therapist';
  const list = service === 'care_management' ? 'caseload' : 'SPICE clients';
  return `They share ${f.object} with the ${lead}: ${f.subject} joins their ${list} and they can open this record.`;
}

/** Transportation consent. */
export function consentIntro(p: Pronouns): string {
  const f = forms(p);
  return `Whether you may drive ${f.object}. Until it is recorded, no one drives ${f.object}.`;
}

/** Code status. */
export function codeStatusIntro(p: Pronouns): string {
  const f = forms(p);
  const generic = f.subject === 'this client';
  const wants = f.plural ? 'want' : 'wants';
  const possessive = generic ? 'their' : f.possessive;
  return `What ${f.subject} ${wants} if ${possessive} heart or breathing stops.`;
}

/** The archive confirm's body, naming the client. */
export function archiveBody(name: string, p: Pronouns): string {
  const f = forms(p);
  return `Archiving hides ${name} from the roster. Nothing is deleted: the record, notes and history stay, and you can restore ${f.object} at any time.`;
}

/** The restore confirm's body, naming the client. */
export function restoreBody(name: string): string {
  return `Restoring returns ${name} to the roster.`;
}

/** YYYY-MM-DD naming a calendar day. */
function isRealDate(v: string): boolean {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  if (m === null) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const day = new Date(Date.UTC(y, mo - 1, d));
  return day.getUTCFullYear() === y && day.getUTCMonth() === mo - 1 && day.getUTCDate() === d;
}

/** "{n} years old today." on `today` ("1 year old today." for one); null
 *  for a date later than today or a value that is not a date. */
export function ageLine(dob: string, today: string): string | null {
  if (!isRealDate(dob) || !isRealDate(today) || dob > today) return null;
  const years = ageOn(dob, today);
  return `${years} year${years === 1 ? '' : 's'} old today.`;
}
